/**
 * ReporteVivienda.jsx
 * ──────────────────────────────────────────────────────────────────────
 * Reporte generativo de cartera hipotecaria (vivienda) IFRS 9.
 * Secciones: Resumen · Calidad · Migración · Por Destino ·
 *            Territorio · Originación · Riesgo
 */

import React, { useState, useCallback, useEffect, useRef, memo } from "react";
import {
  COLOR, fmt, fmtB, fmtPct, fmtN,
  KpiCard, BarRow, SectionHeader, InsightBox,
  MapaEstados, MapaLeyenda,
} from "./ReporteComponents";

const API_URL = process.env.REACT_APP_BACKEND_URL || "https://cnbv-agente-196915693363.us-central1.run.app";

const PERIODOS = [
  "202607","202606","202605","202604","202603","202602","202601",
  "202512","202511","202510","202509","202508","202507",
];

const INSTITUCIONES_VIV = [
  "Afirme",
  "BBVA México",
  "Banamex",
  "Banca Mifel",
  "Banco Azteca",
  "Banco del Bajío",
  "Bancoppel",
  "Banorte",
  "Banregio",
  "Bansí",
  "CIBanco",
  "HSBC",
  "Inbursa",
  "Invex",
  "Kapital (Antes Autofin)",
  "Multiva",
  "Santander",
  "Scotiabank",
  "Ve por Más",
];

const SECCIONES = [
  { id: "resumen",    icon: "📋", label: "Resumen"      },
  { id: "calidad",    icon: "◉",  label: "Calidad"      },
  { id: "rollrates",  icon: "⟳",  label: "Migración"    },
  { id: "destino",    icon: "◆",  label: "Por Destino"  },
  { id: "territorio", icon: "🗺️", label: "Territorio"   },
  { id: "originacion",icon: "⊕",  label: "Originación"  },
  { id: "riesgo",     icon: "🔍", label: "Riesgo"       },
];

const fmtImor = n => n == null ? "—" : `${fmt(n, 2)}%`;
const fmtLtv  = n => n == null ? "—" : `${fmt(n, 1)}%`;

// ══════════════════════════════════════════════════════════════════════
// EXPORTACIÓN HTML — informe autónomo (paridad con Consumo/Comercial)
// ══════════════════════════════════════════════════════════════════════
function generarHTMLExport(reporte) {
  const s   = reporte.secciones || {};
  const per = String(reporte.periodo);
  const perFmt = `${per.slice(0,4)}-${per.slice(4)}`;
  const ent = reporte.nombre_entidad || "Total Banca Múltiple";

  const f   = (n, d = 1) => (n ?? 0).toFixed(d);
  const fB  = n => `${f(n, 2)} mmdp`;
  const fN  = n => (n ?? 0).toLocaleString("es-MX");
  const fP  = n => n == null ? "—" : `${n >= 0 ? "+" : ""}${f(n, 1)}%`;
  const fI  = n => n == null ? "—" : `${f(n, 2)}%`;
  const fL  = n => n == null ? "—" : `${f(n, 1)}%`;
  const esc = v => String(v ?? "").replace(/&/g,"&amp;").replace(/</g,"&lt;").replace(/>/g,"&gt;");

  const C = {
    bg:"#070c12", surface:"#0d1520", card:"#111b28", border:"#1a2535",
    text:"#dde4ee", muted:"#8aa4b8", dim:"#556a7a",
    gold:"#c8a96e", goldDim:"#8a6d3b",
    green:"#34d399", red:"#f87171", blue:"#60a5fa", amber:"#fbbf24",
  };

  const kpiCard = (label, value, delta, dl, accent) =>
    `<div style="background:${C.card};border-radius:6px;padding:12px 16px;border:1px solid ${C.border};flex:1;min-width:150px;">
      <div style="font-size:10px;color:${C.muted};font-weight:700;font-family:monospace;text-transform:uppercase;letter-spacing:.6px;margin-bottom:6px;">${esc(label)}</div>
      <div style="font-size:22px;font-weight:800;color:${accent||C.gold};font-family:monospace;">${esc(value)}</div>
      ${delta != null ? `<div style="font-size:11px;margin-top:5px;font-family:monospace;color:${delta>0?C.green:delta<0?C.red:C.muted};">${delta>0?"▲":delta<0?"▼":"─"} ${fP(delta)}${dl?` <span style="color:${C.dim}">${esc(dl)}</span>`:""}</div>` : ""}
    </div>`;

  const insightBox = text => !text ? "" :
    `<div style="background:${C.gold}0d;border:1px solid ${C.goldDim};border-radius:6px;padding:10px 14px;font-size:12px;line-height:1.7;color:${C.text};margin-top:10px;">
      <div style="font-size:9px;font-weight:700;font-family:monospace;background:${C.gold}22;color:${C.gold};display:inline-block;padding:1px 8px;border-radius:4px;margin-bottom:6px;border:1px solid ${C.goldDim};">✦ Comentario analítico</div>
      <div style="white-space:pre-wrap;">${esc(text)}</div>
    </div>`;

  const secHeader = (icon, title) =>
    `<div style="padding:10px 0;margin-bottom:14px;border-bottom:2px solid ${C.gold}44;">
      <span style="font-size:16px;margin-right:8px;">${icon}</span>
      <span style="font-weight:700;font-size:13px;color:${C.gold};font-family:monospace;letter-spacing:0.05em;text-transform:uppercase;">${esc(title)}</span>
    </div>`;

  const barRow = (label, pct, right, color) =>
    `<div style="display:flex;align-items:center;gap:8px;margin-bottom:6px;">
      <div style="width:150px;font-size:11px;color:${C.muted};font-family:monospace;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;flex-shrink:0;">${esc(label)}</div>
      <div style="flex:1;height:6px;background:${C.border};border-radius:4px;overflow:hidden;"><div style="width:${Math.min(100,Math.max(1,pct||0))}%;height:100%;background:${color||C.gold};border-radius:4px;"></div></div>
      <div style="font-size:11px;color:${C.muted};width:180px;font-family:monospace;text-align:right;flex-shrink:0;">${esc(right)}</div>
    </div>`;

  const subH = t => `<div style="font-size:10px;font-weight:700;color:${C.muted};font-family:monospace;text-transform:uppercase;letter-spacing:.5px;margin-bottom:10px;padding-bottom:6px;border-bottom:1px solid ${C.border};">${esc(t)}</div>`;
  const thS  = `padding:8px 12px;text-align:left;color:${C.muted};font-weight:600;border-bottom:1px solid ${C.border};font-size:10px;font-family:monospace;`;
  const tdS  = `padding:7px 12px;font-size:10px;font-family:monospace;`;

  // 1. RESUMEN
  const k = s.resumen?.kpis || {};
  const secResumen = `<section style="margin-bottom:32px;">
    ${secHeader("📋","Resumen ejecutivo")}
    <div style="display:flex;gap:10px;margin-bottom:10px;flex-wrap:wrap;">
      ${kpiCard("Saldo total",     fB(k.saldo_total_b),   k.delta_total_mom, "MoM")}
      ${kpiCard("Cartera vigente", fB(k.saldo_vigente_b))}
      ${kpiCard("Cartera vencida", fB(k.saldo_vencido_b), k.delta_venc_mom, "MoM", C.red)}
      ${kpiCard("IMOR",            fI(k.imor), null, null, k.imor>3?C.red:C.gold)}
      ${kpiCard("ICOR",            fI(k.icor))}
    </div>
    <div style="display:flex;gap:10px;margin-bottom:16px;flex-wrap:wrap;">
      ${kpiCard("Núm. créditos",   fN(k.num_creditos), k.delta_cred_mom, "MoM")}
      ${kpiCard("Saldo promedio",  k.saldo_promedio_mxn!=null?`$${f(k.saldo_promedio_mxn/1000,0)}K`:"—")}
      ${kpiCard("Tasa ponderada",  fI(k.tasa_ponderada))}
      ${kpiCard("Crec. YoY nominal", fP(k.delta_total_yoy), null, null, k.delta_total_yoy>=0?C.green:C.red)}
      ${k.delta_total_yoy_real!=null ? kpiCard("Crec. YoY real", fP(k.delta_total_yoy_real), null, null, k.delta_total_yoy_real>=0?C.green:C.red) : ""}
    </div>
    ${insightBox(s.resumen?.narrativa)}
  </section>`;

  // 2. CALIDAD
  const cal = s.calidad || {};
  const et = cal.por_etapa || {};
  const segsC = cal.por_segmento || [];
  const maxSegC = segsC[0]?.saldo_b || 0.001;
  const secCalidad = `<section style="margin-bottom:32px;">
    ${secHeader("◉","Calidad de Cartera — IFRS 9")}
    <div style="display:flex;gap:10px;margin-bottom:16px;flex-wrap:wrap;">
      ${kpiCard("Etapa 1 — Normal",    fB(et.etapa1?.saldo_b), et.etapa1?.delta_mom, "MoM", C.green)}
      ${kpiCard("Etapa 2 — Deterioro", fB(et.etapa2?.saldo_b), et.etapa2?.delta_mom, "MoM", C.amber)}
      ${kpiCard("Etapa 3 — Vencido",   fB(et.etapa3?.saldo_b), et.etapa3?.delta_mom, "MoM", C.red)}
    </div>
    ${subH("Por segmento de vivienda")}
    ${segsC.length===0 ? `<div style="font-size:11px;color:${C.dim};font-family:monospace;">Sin datos</div>` :
      segsC.map(seg => barRow(seg.segmento||"—", (seg.saldo_b/maxSegC)*100,
        `${fB(seg.saldo_b)} · IMOR ${fI(seg.imor)} · ${fN(seg.num_creditos)} créd.`, C.blue)).join("")}
  </section>`;

  // 3. MIGRACIÓN
  const rr = s.migracion_saldos || {};
  const mat = rr.matriz_actual || {};
  const hist = rr.historico || [];
  const secRoll = `<section style="margin-bottom:32px;">
    ${secHeader("⟳","Migración de Saldos por Etapa IFRS 9")}
    <div style="display:flex;gap:10px;margin-bottom:16px;flex-wrap:wrap;">
      ${kpiCard("Migración E1 → E2", mat.roll_e1_e2_pct!=null?`${f(mat.roll_e1_e2_pct,4)}%`:"—", null, null, C.amber)}
      ${kpiCard("Migración E2 → E3", mat.roll_e2_e3_pct!=null?`${f(mat.roll_e2_e3_pct,4)}%`:"—", null, null, C.red)}
      ${kpiCard("Tasa de cura E3",   mat.cure_rate_pct!=null ?`${f(mat.cure_rate_pct,4)}%`:"—",  null, null, C.green)}
    </div>
    ${insightBox(rr.narrativa)}
    ${hist.length>0 ? `
    <div style="margin-top:14px;">${subH("Histórico de tasas de migración (saldos agregados)")}</div>
    <table style="width:100%;border-collapse:collapse;">
      <thead><tr>
        <th style="${thS}">Periodo</th>
        ${["Etapa1 (mmdp)","Etapa2 (mmdp)","Etapa3 (mmdp)","Roll E1→E2","Roll E2→E3","Cura E3"].map(h=>`<th style="${thS}text-align:right;">${h}</th>`).join("")}
      </tr></thead>
      <tbody>${[...hist].reverse().map(h=>`
        <tr style="border-bottom:1px solid ${C.border}33;">
          <td style="${tdS}color:${C.gold};text-align:right;">${String(h.periodo).slice(0,4)}-${String(h.periodo).slice(4)}</td>
          <td style="${tdS}color:${C.green};text-align:right;">${fB(h.e1_b)}</td>
          <td style="${tdS}color:${C.amber};text-align:right;">${fB(h.e2_b)}</td>
          <td style="${tdS}color:${C.red};text-align:right;">${fB(h.e3_b)}</td>
          <td style="${tdS}color:${C.amber};text-align:right;">${h.roll_e1_e2!=null?`${f(h.roll_e1_e2,4)}%`:"—"}</td>
          <td style="${tdS}color:${C.red};text-align:right;">${h.roll_e2_e3!=null?`${f(h.roll_e2_e3,4)}%`:"—"}</td>
          <td style="${tdS}color:${C.green};text-align:right;">${h.cure_rate!=null?`${f(h.cure_rate,4)}%`:"—"}</td>
        </tr>`).join("")}
      </tbody>
    </table>` : ""}
    <div style="font-size:9px;color:${C.dim};font-family:monospace;margin-top:8px;">* Tasas implícitas sobre variación de saldos agregados entre periodos consecutivos, no sobre cohortes individuales.</div>
  </section>`;

  // 4. POR DESTINO
  const dest = s.destino || {};
  const porD = dest.por_destino || [];
  const maxDsaldo = porD[0]?.saldo_b || 0.001;
  const maxDimor  = Math.max(...porD.map(d=>d.imor||0), 0.001);
  const secDestino = `<section style="margin-bottom:32px;">
    ${secHeader("◆","Por Destino de Crédito")}
    <div style="display:grid;grid-template-columns:1fr 1fr;gap:24px;margin-bottom:16px;">
      <div>${subH("Por destino — saldo total")}
        ${porD.length===0?`<div style="font-size:11px;color:${C.dim};font-family:monospace;">Sin datos</div>`:
          porD.map(d=>barRow(d.destino||"—",(d.saldo_b/maxDsaldo)*100,`${fB(d.saldo_b)} · ${f(d.pct_saldo,1)}%`,C.blue)).join("")}
      </div>
      <div>${subH("IMOR por destino")}
        ${porD.length===0?`<div style="font-size:11px;color:${C.dim};font-family:monospace;">Sin datos</div>`:
          porD.map(d=>barRow(d.destino||"—",(d.imor/maxDimor)*100,fI(d.imor),d.imor>5?C.red:d.imor>3?C.amber:C.green)).join("")}
      </div>
    </div>
    ${porD.length>0?`
    <table style="width:100%;border-collapse:collapse;">
      <thead><tr>
        <th style="${thS}">Destino de crédito</th>
        ${["Saldo (mmdp)","Vencido (mmdp)","IMOR","Créditos","% Saldo","Tasa pond."].map(h=>`<th style="${thS}text-align:right;">${h}</th>`).join("")}
      </tr></thead>
      <tbody>${porD.map(d=>`
        <tr style="border-bottom:1px solid ${C.border}33;">
          <td style="${tdS}color:${C.text};">${esc(d.destino)}</td>
          <td style="${tdS}color:${C.gold};text-align:right;">${fB(d.saldo_b)}</td>
          <td style="${tdS}color:${C.red};text-align:right;">${fB(d.vencido_b)}</td>
          <td style="${tdS}text-align:right;color:${d.imor>5?C.red:d.imor>3?C.amber:C.green};font-weight:700;">${fI(d.imor)}</td>
          <td style="${tdS}color:${C.muted};text-align:right;">${fN(d.num_creditos)}</td>
          <td style="${tdS}color:${C.muted};text-align:right;">${f(d.pct_saldo,1)}%</td>
          <td style="${tdS}color:${C.muted};text-align:right;">${d.tasa_pond!=null?`${f(d.tasa_pond,2)}%`:"—"}</td>
        </tr>`).join("")}
      </tbody>
    </table>`:""}
  </section>`;

  // 5. TERRITORIO (ranking estatal + tabla municipios)
  const terr = Array.isArray(s.territorio) ? s.territorio : [];
  const terrSorted = [...terr].sort((a,b)=>(b.saldo_b||0)-(a.saldo_b||0));
  const maxT = terrSorted[0]?.saldo_b || 0.001;
  const munRaw = s.territorio_municipio;
  const munDatos = Array.isArray(munRaw) ? munRaw : (munRaw?.datos || []);
  const munAdv = munRaw?.advertencia || null;
  const secTerritorio = `<section style="margin-bottom:32px;">
    ${secHeader("🗺️","Territorio — Distribución por Estado")}
    <div style="display:grid;grid-template-columns:1fr 1fr;gap:24px;margin-bottom:20px;">
      <div>${subH("Ranking por saldo hipotecario")}
        ${terrSorted.length===0?`<div style="font-size:11px;color:${C.dim};font-family:monospace;">Sin datos</div>`:
          terrSorted.map((t,i)=>barRow(`${i+1}. ${(t.estado||"—").toUpperCase()}`,(t.saldo_b/maxT)*100,`${fB(t.saldo_b)} · IMOR ${fI(t.imor)}`,C.gold)).join("")}
      </div>
      <div>${subH("Detalle por estado")}
        <table style="width:100%;border-collapse:collapse;">
          <thead><tr>
            <th style="${thS}">Estado</th>
            ${["Saldo","Vencido","IMOR","Créditos"].map(h=>`<th style="${thS}text-align:right;">${h}</th>`).join("")}
          </tr></thead>
          <tbody>${terrSorted.slice(0,15).map(t=>`
            <tr style="border-bottom:1px solid ${C.border}33;">
              <td style="${tdS}color:${C.text};">${esc(t.estado)}</td>
              <td style="${tdS}color:${C.gold};text-align:right;">${fB(t.saldo_b)}</td>
              <td style="${tdS}color:${C.red};text-align:right;">${fB(t.vencido_b)}</td>
              <td style="${tdS}text-align:right;color:${t.imor>5?C.red:t.imor>3?C.amber:C.green};font-weight:700;">${fI(t.imor)}</td>
              <td style="${tdS}color:${C.muted};text-align:right;">${fN(t.num_creditos)}</td>
            </tr>`).join("")}
          </tbody>
        </table>
      </div>
    </div>
    ${subH("Top municipios por saldo hipotecario")}
    ${munAdv?`<div style="font-size:9px;color:${C.dim};font-family:monospace;margin-bottom:6px;">⚠ ${esc(munAdv)}</div>`:""}
    ${munDatos.length===0?`<div style="font-size:11px;color:${C.dim};font-family:monospace;">Sin datos municipales disponibles.</div>`:`
    <table style="width:100%;border-collapse:collapse;">
      <thead><tr>
        <th style="${thS}">#</th><th style="${thS}">Municipio</th><th style="${thS}">Estado</th>
        ${["Saldo (mmdp)","Vencido (mmdp)","IMOR","Créditos"].map(h=>`<th style="${thS}text-align:right;">${h}</th>`).join("")}
      </tr></thead>
      <tbody>${munDatos.slice(0,50).map((m,i)=>`
        <tr style="border-bottom:1px solid ${C.border}33;">
          <td style="${tdS}color:${C.dim};">${i+1}</td>
          <td style="${tdS}color:${C.text};">${esc(m.municipio)}</td>
          <td style="${tdS}color:${C.muted};">${esc(m.estado)}</td>
          <td style="${tdS}color:${C.gold};text-align:right;">${fB(m.saldo_b)}</td>
          <td style="${tdS}color:${C.red};text-align:right;">${fB(m.vencido_b)}</td>
          <td style="${tdS}text-align:right;color:${m.imor>5?C.red:m.imor>3?C.amber:C.green};font-weight:700;">${fI(m.imor)}</td>
          <td style="${tdS}color:${C.muted};text-align:right;">${fN(m.num_creditos)}</td>
        </tr>`).join("")}
      </tbody>
    </table>`}
  </section>`;

  // 6. ORIGINACIÓN
  const orig = s.originacion || {};
  const oK = orig.kpis || {};
  const oSeg = orig.por_segmento || [];
  const oGen = orig.por_genero || [];
  const oDest = orig.por_destino || [];
  const maxOSeg = oSeg[0]?.monto_orig_b || 0.001;
  const maxOGen = oGen[0]?.monto_orig_b || 0.001;
  const maxODest = oDest[0]?.monto_orig_b || 0.001;
  const secOrig = `<section style="margin-bottom:32px;">
    ${secHeader("⊕","Originación de Crédito")}
    <div style="display:flex;gap:10px;margin-bottom:16px;flex-wrap:wrap;">
      ${kpiCard("Créditos originados", fN(oK.nuevos_creditos), oK.delta_cuentas_mom, "MoM")}
      ${kpiCard("Monto originado",     fB(oK.monto_orig_b),    oK.delta_saldo_mom,   "MoM")}
      ${kpiCard("LTV promedio",        fL(oK.ltv_promedio_pct))}
      ${kpiCard("Tasa ponderada",      oK.tasa_pond!=null?`${f(oK.tasa_pond,2)}%`:"—")}
    </div>
    <div style="display:grid;grid-template-columns:1fr 1fr;gap:24px;margin-bottom:16px;">
      <div>${subH("Por segmento de vivienda")}
        ${oSeg.length===0?`<div style="font-size:11px;color:${C.dim};font-family:monospace;">Sin datos</div>`:
          oSeg.map(g=>barRow(g.segmento||"—",(g.monto_orig_b/maxOSeg)*100,`${fB(g.monto_orig_b)} · LTV ${fL(g.ltv_promedio_pct)}`,C.blue)).join("")}
      </div>
      <div>${subH("Por género")}
        ${oGen.length===0?`<div style="font-size:11px;color:${C.dim};font-family:monospace;">Sin datos</div>`:
          oGen.map(g=>barRow(g.genero||"—",(g.monto_orig_b/maxOGen)*100,`${fB(g.monto_orig_b)} · ${f(g.pct_monto,1)}%`,
            (g.genero||"").toLowerCase().includes("emen")?"#ec4899":(g.genero||"").toLowerCase().includes("ascu")?C.blue:C.muted)).join("")}
      </div>
    </div>
    ${subH("Por destino de crédito (monto originado)")}
    ${oDest.length===0?`<div style="font-size:11px;color:${C.dim};font-family:monospace;">Sin datos</div>`:
      oDest.map(d=>barRow(d.destino||"—",(d.monto_orig_b/maxODest)*100,`${fB(d.monto_orig_b)} · LTV ${fL(d.ltv_promedio_pct)} · ${f(d.pct_monto,1)}%`,C.gold)).join("")}
  </section>`;

  // 7. RIESGO (HHI + Anomalías)
  const conc = s.concentracion || {};
  const anom = s.anomalias || {};
  const catColor = c => c==="Alta"?C.red:c==="Moderada"?C.amber:C.green;
  const secRiesgo = `<section style="margin-bottom:32px;">
    ${secHeader("🔍","Concentración y Anomalías")}
    <div style="display:grid;grid-template-columns:1fr 1fr;gap:20px;">
      <div>${subH("Concentración HHI — por segmento")}
        ${conc.insight?`<div style="font-size:11px;color:${C.muted};line-height:1.6;margin-bottom:10px;">${esc(conc.insight)}</div>`:""}
        ${(conc.filas||[]).map(fr=>`
          <div style="display:flex;justify-content:space-between;padding:5px 0;border-bottom:1px solid ${C.border}33;font-size:11px;font-family:monospace;">
            <div style="flex:1;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;padding-right:8px;">
              <span style="color:${catColor(fr.categoria_hhi)};margin-right:6px;">●</span><span style="color:${C.text};">${esc(fr.segmento_vivienda)}</span>
            </div>
            <div style="text-align:right;flex-shrink:0;">
              <span style="color:${catColor(fr.categoria_hhi)};font-weight:700;">${f(fr.hhi_actual,0)}</span>
              <span style="color:${C.dim};margin-left:6px;">${fr.direccion==="Aumenta"?"▲":fr.direccion==="Disminuye"?"▼":"─"}</span>
              <span style="color:${C.dim};margin-left:6px;">${esc(fr.banco_dominante)} (${f(fr.cuota_dominante_pct,1)}%)</span>
            </div>
          </div>`).join("")}
      </div>
      <div>${subH("Anomalías estadísticas IMOR")}
        ${(anom.filas||[]).length===0?`<div style="font-size:11px;color:${C.green};font-family:monospace;">Sin anomalías detectadas en el periodo.</div>`:
          (anom.filas||[]).map(fr=>`
          <div style="padding:7px 0;border-bottom:1px solid ${C.border}33;font-size:11px;font-family:monospace;">
            <div style="display:flex;justify-content:space-between;margin-bottom:2px;">
              <span style="color:${C.text};font-weight:600;">${esc(fr.nombre_entidad)}</span>
              <span style="color:${fr.z_score>0?C.red:C.green};font-weight:700;">z=${f(fr.z_score,2)}</span>
            </div>
            <div style="color:${C.muted};">${esc(fr.actividad_economica)} · IMOR ${fI(fr.imor)} · Δ ${fr.delta_imor_pp>0?"+":""}${f(fr.delta_imor_pp,2)} pp</div>
            <div style="margin-top:2px;"><span style="font-size:10px;padding:1px 6px;border-radius:3px;background:${fr.z_score>0?C.red:C.green}22;color:${fr.z_score>0?C.red:C.green};border:1px solid ${fr.z_score>0?C.red:C.green}44;">${esc(fr.tipo_anomalia)}</span></div>
          </div>`).join("")}
      </div>
    </div>
  </section>`;

  return `<!DOCTYPE html>
<html lang="es">
<head>
<meta charset="UTF-8"/>
<meta name="viewport" content="width=device-width,initial-scale=1"/>
<title>Cartera de Vivienda ${esc(ent)} — ${perFmt}</title>
<style>
  *{box-sizing:border-box;margin:0;padding:0}
  body{background:${C.bg};color:${C.text};font-family:'DM Sans',system-ui,sans-serif;padding:32px 40px;max-width:1100px;margin:auto;}
  h1{font-size:20px;font-weight:800;color:${C.gold};font-family:monospace;letter-spacing:.05em;}
  @media print{body{padding:16px}section{page-break-inside:avoid}}
</style>
</head>
<body>
  <div style="margin-bottom:28px;padding-bottom:16px;border-bottom:2px solid ${C.gold}44;">
    <h1>🏠 CARTERA DE VIVIENDA IFRS 9</h1>
    <div style="font-size:13px;color:${C.muted};font-family:monospace;margin-top:6px;">
      ${esc(ent)} · Periodo ${esc(perFmt)} · Generado ${new Date().toLocaleString("es-MX")}
    </div>
  </div>
  ${secResumen}
  ${secCalidad}
  ${secRoll}
  ${secDestino}
  ${secTerritorio}
  ${secOrig}
  ${secRiesgo}
  <div style="margin-top:40px;padding-top:16px;border-top:1px solid ${C.border};font-size:9px;color:${C.dim};font-family:monospace;">
    Reporte generado automáticamente por EMRISK · CNBV · Cartera de Vivienda · Vista: cnbv_viv_saldo_vw
  </div>
</body>
</html>`;
}

// ══════════════════════════════════════════════════════════════════════
// SECCIÓN: RESUMEN
// ══════════════════════════════════════════════════════════════════════
function SeccionResumen({ s }) {
  const k   = s?.resumen?.kpis || {};
  const nav = s?.resumen?.narrativa || "";
  return (
    <div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 12, marginBottom: 16 }}>
        <KpiCard label="Saldo total"     value={fmtB(k.saldo_total_b)}   unit=""  delta={k.delta_total_mom} />
        <KpiCard label="Cartera vigente" value={fmtB(k.saldo_vigente_b)} unit=""  delta={null} />
        <KpiCard label="Cartera vencida" value={fmtB(k.saldo_vencido_b)} unit=""  delta={k.delta_venc_mom}
          accentColor={k.saldo_vencido_b > 0 ? COLOR.red : COLOR.green} />
        <KpiCard label="IMOR"            value={k.imor != null ? `${k.imor.toFixed(2)}%` : "—"}  unit=""  delta={null}
          accentColor={k.imor > 3 ? COLOR.red : COLOR.gold} />
        <KpiCard label="ICOR"            value={k.icor != null ? `${k.icor.toFixed(2)}%` : "—"}  unit=""  delta={null} />
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 12, marginBottom: 20 }}>
        <KpiCard label="Núm. créditos"    value={fmtN(k.num_creditos)}    unit=""  delta={k.delta_cred_mom} />
        <KpiCard label="Saldo promedio"   value={k.saldo_promedio_mxn != null ? `$${(k.saldo_promedio_mxn/1000).toFixed(0)}K` : "—"} unit="" delta={null} />
        <KpiCard label="Tasa ponderada"   value={k.tasa_ponderada != null ? `${k.tasa_ponderada.toFixed(2)}%` : "—"} unit="" delta={null} />
        <KpiCard label="Crec. YoY nominal" value={k.delta_total_yoy != null ? `${k.delta_total_yoy >= 0 ? "+" : ""}${k.delta_total_yoy.toFixed(1)}%` : "—"} unit="" delta={null}
          accentColor={k.delta_total_yoy >= 0 ? COLOR.green : COLOR.red} />
        {k.delta_total_yoy_real != null && (
          <KpiCard label="Crec. YoY real" value={`${k.delta_total_yoy_real >= 0 ? "+" : ""}${k.delta_total_yoy_real.toFixed(1)}%`} unit="" delta={null}
            accentColor={k.delta_total_yoy_real >= 0 ? COLOR.green : COLOR.red} />
        )}
      </div>
      {nav && <InsightBox text={nav} />}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════
// SECCIÓN: CALIDAD
// ══════════════════════════════════════════════════════════════════════
function SeccionCalidad({ s }) {
  const cal = s?.calidad || {};
  const etapas = cal.por_etapa || {};
  const segs   = cal.por_segmento || [];

  const e1 = etapas.etapa1 || {};
  const e2 = etapas.etapa2 || {};
  const e3 = etapas.etapa3 || {};
  const maxSeg = segs[0]?.saldo_b || 0.001;

  const COLORS = [COLOR.blue, COLOR.gold, "#a78bfa", COLOR.green, COLOR.amber, COLOR.muted, "#ec4899", "#f97316"];

  return (
    <div>
      {/* Etapas IFRS 9 */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 12, marginBottom: 20 }}>
        <KpiCard label="Etapa 1 — Normal"    value={fmtB(e1.saldo_b)} unit="" delta={e1.delta_mom}
          accentColor={COLOR.green} />
        <KpiCard label="Etapa 2 — Deterioro" value={fmtB(e2.saldo_b)} unit="" delta={e2.delta_mom}
          accentColor={COLOR.amber} />
        <KpiCard label="Etapa 3 — Vencido"   value={fmtB(e3.saldo_b)} unit="" delta={e3.delta_mom}
          accentColor={COLOR.red} />
      </div>

      {/* Por segmento de vivienda */}
      <SectionHeader icon="🏠" title="Por Segmento de Vivienda" />
      {segs.length === 0
        ? <div style={{ fontSize: 14, color: COLOR.dim, fontFamily: "monospace" }}>Sin datos</div>
        : segs.map((seg, i) => (
          <div key={i} style={{ marginBottom: 10 }}>
            <BarRow
              label={seg.segmento || "—"}
              value={seg.saldo_b || 0}
              maxValue={maxSeg}
              color={COLORS[i % COLORS.length]}
              rightLabel={`${fmtB(seg.saldo_b)} · IMOR ${fmtImor(seg.imor)} · ${fmtN(seg.num_creditos)} créd.`}
            />
          </div>
        ))
      }
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════
// SECCIÓN: MIGRACIÓN (ROLL RATES)
// ══════════════════════════════════════════════════════════════════════
function SeccionRollRates({ s }) {
  const rr      = s?.migracion_saldos || {};
  const matriz  = rr.matriz_actual || {};
  const hist    = rr.historico || [];
  const narrativa = rr.narrativa || "";

  const cell = (v, color) => (
    <span style={{ color: v == null ? COLOR.dim : color || COLOR.text, fontFamily: "monospace" }}>
      {v == null ? "N/D" : `${v.toFixed(4)}%`}
    </span>
  );

  return (
    <div>
      {/* Matriz actual */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 12, marginBottom: 20 }}>
        <KpiCard label="Migración E1 → E2" value={matriz.roll_e1_e2_pct != null ? `${matriz.roll_e1_e2_pct.toFixed(4)}%` : "—"} unit="" delta={null} accentColor={COLOR.amber} />
        <KpiCard label="Migración E2 → E3" value={matriz.roll_e2_e3_pct != null ? `${matriz.roll_e2_e3_pct.toFixed(4)}%` : "—"} unit="" delta={null} accentColor={COLOR.red} />
        <KpiCard label="Tasa de cura E3"   value={matriz.cure_rate_pct  != null ? `${matriz.cure_rate_pct.toFixed(4)}%`  : "—"} unit="" delta={null} accentColor={COLOR.green} />
      </div>

      {narrativa && <InsightBox text={narrativa} />}

      {/* Histórico tabular */}
      {hist.length > 0 && (
        <div style={{ marginTop: 16, overflowX: "auto" }}>
          <div style={{ fontSize: 13, color: COLOR.muted, fontFamily: "monospace",
                        textTransform: "uppercase", letterSpacing: ".5px", marginBottom: 8 }}>
            Histórico de tasas de migración (saldos agregados)
          </div>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14, fontFamily: "monospace" }}>
            <thead>
              <tr style={{ borderBottom: `1px solid ${COLOR.border}` }}>
                {["Periodo", "Etapa1 (mmdp)", "Etapa2 (mmdp)", "Etapa3 (mmdp)", "Roll E1→E2", "Roll E2→E3", "Cura E3"].map(h => (
                  <th key={h} style={{ padding: "5px 10px", color: COLOR.muted, fontWeight: 600, textAlign: "right" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {[...hist].reverse().map((h, i) => (
                <tr key={i} style={{ borderBottom: `1px solid ${COLOR.border}33`,
                                     background: i === 0 ? `${COLOR.gold}08` : "none" }}>
                  <td style={{ padding: "5px 10px", color: COLOR.gold, textAlign: "right" }}>
                    {`${String(h.periodo).slice(0,4)}-${String(h.periodo).slice(4)}`}
                  </td>
                  <td style={{ padding: "5px 10px", color: COLOR.green,  textAlign: "right" }}>{fmtB(h.e1_b)}</td>
                  <td style={{ padding: "5px 10px", color: COLOR.amber,  textAlign: "right" }}>{fmtB(h.e2_b)}</td>
                  <td style={{ padding: "5px 10px", color: COLOR.red,    textAlign: "right" }}>{fmtB(h.e3_b)}</td>
                  <td style={{ padding: "5px 10px", color: COLOR.amber,  textAlign: "right" }}>
                    {h.roll_e1_e2 != null ? `${h.roll_e1_e2.toFixed(4)}%` : "—"}
                  </td>
                  <td style={{ padding: "5px 10px", color: COLOR.red,    textAlign: "right" }}>
                    {h.roll_e2_e3 != null ? `${h.roll_e2_e3.toFixed(4)}%` : "—"}
                  </td>
                  <td style={{ padding: "5px 10px", color: COLOR.green,  textAlign: "right" }}>
                    {h.cure_rate != null ? `${h.cure_rate.toFixed(4)}%` : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════
// SECCIÓN: POR DESTINO DE CRÉDITO
// ══════════════════════════════════════════════════════════════════════
function SeccionDestino({ s }) {
  const dest    = s?.destino || {};
  const por_d   = dest.por_destino || [];
  const top_imor = dest.top_imor  || [];

  const maxSaldo = por_d[0]?.saldo_b || 0.001;
  const COLORS   = [COLOR.blue, COLOR.gold, "#a78bfa", COLOR.green, COLOR.amber,
                    COLOR.muted, "#ec4899", "#f97316", "#34d399", "#60a5fa"];

  return (
    <div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24 }}>
        {/* Top saldo */}
        <div>
          <div style={{ fontSize: 13, color: COLOR.muted, fontFamily: "monospace",
                        textTransform: "uppercase", letterSpacing: ".5px",
                        marginBottom: 10, paddingBottom: 6, borderBottom: `1px solid ${COLOR.border}` }}>
            Por destino — saldo total
          </div>
          {por_d.length === 0
            ? <div style={{ fontSize: 14, color: COLOR.dim, fontFamily: "monospace" }}>Sin datos</div>
            : por_d.map((d, i) => (
              <BarRow key={i}
                label={d.destino || "—"}
                value={d.saldo_b || 0}
                maxValue={maxSaldo}
                color={COLORS[i % COLORS.length]}
                rightLabel={`${fmtB(d.saldo_b)} · ${d.pct_saldo?.toFixed(1)}%`}
              />
            ))
          }
        </div>

        {/* IMOR por destino */}
        <div>
          <div style={{ fontSize: 13, color: COLOR.muted, fontFamily: "monospace",
                        textTransform: "uppercase", letterSpacing: ".5px",
                        marginBottom: 10, paddingBottom: 6, borderBottom: `1px solid ${COLOR.border}` }}>
            IMOR por destino
          </div>
          {por_d.length === 0
            ? <div style={{ fontSize: 14, color: COLOR.dim, fontFamily: "monospace" }}>Sin datos</div>
            : (() => {
                const maxImor = Math.max(...por_d.map(d => d.imor || 0)) || 0.001;
                return por_d.map((d, i) => (
                  <BarRow key={i}
                    label={d.destino || "—"}
                    value={d.imor || 0}
                    maxValue={maxImor}
                    color={d.imor > 5 ? COLOR.red : d.imor > 3 ? COLOR.amber : COLOR.green}
                    rightLabel={fmtImor(d.imor)}
                  />
                ));
              })()
          }
        </div>
      </div>

      {/* Tabla detalle */}
      {por_d.length > 0 && (
        <div style={{ marginTop: 20, overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14, fontFamily: "monospace" }}>
            <thead>
              <tr style={{ borderBottom: `1px solid ${COLOR.border}` }}>
                {["Destino de crédito", "Saldo (mmdp)", "Vencido (mmdp)", "IMOR", "Créditos", "% Saldo", "Tasa pond."].map(h => (
                  <th key={h} style={{ padding: "5px 10px", color: COLOR.muted, fontWeight: 600, textAlign: "right",
                                       textAlign: h === "Destino de crédito" ? "left" : "right" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {por_d.map((d, i) => (
                <tr key={i} style={{ borderBottom: `1px solid ${COLOR.border}33` }}>
                  <td style={{ padding: "5px 10px", color: COLOR.text, maxWidth: 220, overflow: "hidden",
                                textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{d.destino}</td>
                  <td style={{ padding: "5px 10px", color: COLOR.gold,  textAlign: "right" }}>{fmtB(d.saldo_b)}</td>
                  <td style={{ padding: "5px 10px", color: COLOR.red,   textAlign: "right" }}>{fmtB(d.vencido_b)}</td>
                  <td style={{ padding: "5px 10px", color: d.imor > 5 ? COLOR.red : d.imor > 3 ? COLOR.amber : COLOR.green,
                                textAlign: "right" }}>{fmtImor(d.imor)}</td>
                  <td style={{ padding: "5px 10px", color: COLOR.muted, textAlign: "right" }}>{fmtN(d.num_creditos)}</td>
                  <td style={{ padding: "5px 10px", color: COLOR.muted, textAlign: "right" }}>{d.pct_saldo?.toFixed(1)}%</td>
                  <td style={{ padding: "5px 10px", color: COLOR.muted, textAlign: "right" }}>
                    {d.tasa_pond != null ? `${d.tasa_pond.toFixed(2)}%` : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════
// SECCIÓN: TERRITORIO
// ══════════════════════════════════════════════════════════════════════
function SeccionTerritorio({ s }) {
  const [metrica,  setMetrica]  = useState("saldo_b");
  const [subVista, setSubVista] = useState("estado");

  const data     = (s?.territorio || []).map(d => ({ ...d, nombre_estado: d.estado }));
  const munRaw   = s?.territorio_municipio;
  const munDatos = Array.isArray(munRaw) ? munRaw : (munRaw?.datos || []);

  const metricas = [
    { key: "saldo_b",   label: "Saldo total"    },
    { key: "vencido_b", label: "Cartera vencida" },
    { key: "imor",      label: "IMOR"            },
  ];

  const tooltipFn = useCallback((d, nombre) => `
    <div style="font-family:monospace;padding:6px 8px;min-width:180px;background:#0d1520;border:1px solid #1a2535;border-radius:4px">
      <div style="font-size:12px;font-weight:700;color:#dde4ee;margin-bottom:5px">${nombre}</div>
      <div style="font-size:10px;color:#c8a96e;margin-bottom:4px">Saldo: ${fmtB(d?.saldo_b)}</div>
      <div style="font-size:10px;color:#8aa4b8;line-height:1.7">
        Vencido: ${fmtB(d?.vencido_b)}<br/>
        IMOR: ${fmtImor(d?.imor)}<br/>
        Créditos: ${fmtN(d?.num_creditos)}
      </div>
    </div>
  `, []);

  const btnStyle = active => ({
    padding: "3px 12px", borderRadius: 4, fontSize: 13, fontFamily: "monospace",
    cursor: "pointer", border: `1px solid ${active ? COLOR.gold : COLOR.border}`,
    background: active ? `${COLOR.gold}18` : "none",
    color: active ? COLOR.gold : COLOR.muted,
  });

  const sorted = [...(s?.territorio || [])].sort((a, b) => (b[metrica] || 0) - (a[metrica] || 0));
  const maxVal  = sorted[0]?.[metrica] || 1;

  return (
    <div>
      <div style={{ display: "flex", gap: 6, marginBottom: 14 }}>
        <button onClick={() => setSubVista("estado")}    style={btnStyle(subVista === "estado")}>Estado</button>
        <button onClick={() => setSubVista("municipio")} style={btnStyle(subVista === "municipio")}>Municipio</button>
      </div>

      {/* ── Vista Estado ── */}
      {subVista === "estado" && (
        <>
          <div style={{ display: "flex", gap: 6, marginBottom: 10 }}>
            {metricas.map(m => (
              <button key={m.key} onClick={() => setMetrica(m.key)} style={btnStyle(metrica === m.key)}>
                {m.label}
              </button>
            ))}
          </div>
          <div style={{ display: "flex", gap: 16, alignItems: "flex-start" }}>
            <div style={{ flex: "0 0 58%" }}>
              <div style={{ fontSize: 13, color: COLOR.muted, fontFamily: "monospace", marginBottom: 6 }}>
                Mapa por Estado — {metricas.find(m => m.key === metrica)?.label}
              </div>
              <div style={{ height: 360, borderRadius: 6, overflow: "hidden", marginBottom: 6 }}>
                <MapaEstados data={data} metrica={metrica} tooltipFn={tooltipFn} />
              </div>
              <MapaLeyenda />
            </div>
            <div style={{ flex: 1, overflowY: "auto", maxHeight: 420 }}>
              <div style={{ fontSize: 13, color: COLOR.muted, fontFamily: "monospace",
                            textTransform: "uppercase", letterSpacing: ".5px", marginBottom: 10 }}>
                Ranking — {metricas.find(m => m.key === metrica)?.label}
              </div>
              {sorted.map((item, i) => (
                <div key={i} style={{ marginBottom: 8 }}>
                  <div style={{ display: "flex", justifyContent: "space-between",
                                fontSize: 13, fontFamily: "monospace", marginBottom: 2 }}>
                    <div>
                      <span style={{ color: COLOR.dim, marginRight: 6 }}>{i + 1}</span>
                      <span style={{ color: COLOR.text, fontWeight: 600, textTransform: "uppercase" }}>
                        {item.estado}
                      </span>
                    </div>
                    <span style={{ color: COLOR.gold }}>
                      {metrica === "imor"
                        ? fmtImor(item.imor)
                        : fmtB(item[metrica])}
                    </span>
                  </div>
                  <div style={{ height: 4, background: COLOR.border, borderRadius: 2, overflow: "hidden" }}>
                    <div style={{
                      width: `${Math.min(100, ((item[metrica] || 0) / maxVal) * 100)}%`,
                      height: "100%", background: COLOR.gold, borderRadius: 2,
                    }} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {/* ── Vista Municipio ── */}
      {subVista === "municipio" && (
        munDatos.length === 0
          ? <div style={{ fontSize: 15, color: COLOR.dim, fontFamily: "monospace", padding: "20px 0" }}>
              Sin datos municipales disponibles.
            </div>
          : (
            <div style={{ overflowX: "auto" }}>
              <div style={{ fontSize: 13, color: COLOR.muted, fontFamily: "monospace",
                            marginBottom: 10, textTransform: "uppercase", letterSpacing: ".5px" }}>
                Top municipios por saldo hipotecario
              </div>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14, fontFamily: "monospace" }}>
                <thead>
                  <tr style={{ borderBottom: `1px solid ${COLOR.border}` }}>
                    {["#", "Municipio", "Estado", "Saldo (mmdp)", "Vencido (mmdp)", "IMOR", "Créditos"].map(h => (
                      <th key={h} style={{ padding: "5px 10px", color: COLOR.muted, fontWeight: 600,
                                           textAlign: h === "Municipio" || h === "Estado" || h === "#" ? "left" : "right" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {munDatos.slice(0, 50).map((m, i) => (
                    <tr key={i} style={{ borderBottom: `1px solid ${COLOR.border}33` }}>
                      <td style={{ padding: "5px 10px", color: COLOR.dim }}>{i + 1}</td>
                      <td style={{ padding: "5px 10px", color: COLOR.text, maxWidth: 180,
                                   overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {m.municipio}
                      </td>
                      <td style={{ padding: "5px 10px", color: COLOR.muted }}>{m.estado}</td>
                      <td style={{ padding: "5px 10px", color: COLOR.gold,  textAlign: "right" }}>{fmtB(m.saldo_b)}</td>
                      <td style={{ padding: "5px 10px", color: COLOR.red,   textAlign: "right" }}>{fmtB(m.vencido_b)}</td>
                      <td style={{ padding: "5px 10px", textAlign: "right",
                                   color: m.imor > 5 ? COLOR.red : m.imor > 3 ? COLOR.amber : COLOR.green }}>
                        {fmtImor(m.imor)}
                      </td>
                      <td style={{ padding: "5px 10px", color: COLOR.muted, textAlign: "right" }}>
                        {fmtN(m.num_creditos)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════
// SECCIÓN: ORIGINACIÓN
// ══════════════════════════════════════════════════════════════════════
function SeccionOriginacion({ s }) {
  const orig = s?.originacion || {};
  const kpis = orig.kpis         || {};
  const segs = orig.por_segmento || [];
  const gen  = orig.por_genero   || [];
  const dest = orig.por_destino  || [];

  const maxSeg  = segs[0]?.monto_orig_b || 0.001;
  const maxDest = dest[0]?.monto_orig_b || 0.001;
  const COLORS  = [COLOR.blue, COLOR.gold, "#a78bfa", COLOR.green, COLOR.amber,
                   COLOR.muted, "#ec4899", "#f97316"];

  return (
    <div>
      {/* KPIs */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 12, marginBottom: 20 }}>
        <KpiCard label="Créditos originados" value={fmtN(kpis.nuevos_creditos)}  unit=""  delta={kpis.delta_cuentas_mom} />
        <KpiCard label="Monto originado"     value={fmtB(kpis.monto_orig_b)}     unit=""  delta={kpis.delta_saldo_mom} />
        <KpiCard label="LTV promedio"        value={fmtLtv(kpis.ltv_promedio_pct)} unit="" delta={null} />
        <KpiCard label="Tasa ponderada"      value={kpis.tasa_pond != null ? `${kpis.tasa_pond.toFixed(2)}%` : "—"} unit="" delta={null} />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24, marginBottom: 20 }}>
        {/* Por segmento */}
        <div>
          <div style={{ fontSize: 13, color: COLOR.muted, fontFamily: "monospace",
                        textTransform: "uppercase", letterSpacing: ".5px",
                        marginBottom: 10, paddingBottom: 6, borderBottom: `1px solid ${COLOR.border}` }}>
            Por segmento de vivienda
          </div>
          {segs.length === 0
            ? <div style={{ fontSize: 14, color: COLOR.dim, fontFamily: "monospace" }}>Sin datos</div>
            : segs.map((seg, i) => (
              <BarRow key={i}
                label={seg.segmento || "—"}
                value={seg.monto_orig_b || 0}
                maxValue={maxSeg}
                color={COLORS[i % COLORS.length]}
                rightLabel={`${fmtB(seg.monto_orig_b)} · LTV ${fmtLtv(seg.ltv_promedio_pct)}`}
              />
            ))
          }
        </div>

        {/* Por género */}
        <div>
          <div style={{ fontSize: 13, color: COLOR.muted, fontFamily: "monospace",
                        textTransform: "uppercase", letterSpacing: ".5px",
                        marginBottom: 10, paddingBottom: 6, borderBottom: `1px solid ${COLOR.border}` }}>
            Por género
          </div>
          {gen.length === 0
            ? <div style={{ fontSize: 14, color: COLOR.dim, fontFamily: "monospace" }}>Sin datos</div>
            : (() => {
                const maxG = gen[0]?.monto_orig_b || 0.001;
                return gen.map((g, i) => (
                  <BarRow key={i}
                    label={g.genero || "—"}
                    value={g.monto_orig_b || 0}
                    maxValue={maxG}
                    color={g.genero?.toLowerCase().includes("emen") ? "#ec4899"
                         : g.genero?.toLowerCase().includes("ascu") ? COLOR.blue
                         : COLOR.muted}
                    rightLabel={`${fmtB(g.monto_orig_b)} · ${g.pct_monto?.toFixed(1)}%`}
                  />
                ));
              })()
          }
        </div>
      </div>

      {/* Por destino */}
      <div>
        <div style={{ fontSize: 13, color: COLOR.muted, fontFamily: "monospace",
                      textTransform: "uppercase", letterSpacing: ".5px",
                      marginBottom: 10, paddingBottom: 6, borderBottom: `1px solid ${COLOR.border}` }}>
          Por destino de crédito (monto originado)
        </div>
        {dest.length === 0
          ? <div style={{ fontSize: 14, color: COLOR.dim, fontFamily: "monospace" }}>Sin datos</div>
          : dest.map((d, i) => (
            <BarRow key={i}
              label={d.destino || "—"}
              value={d.monto_orig_b || 0}
              maxValue={maxDest}
              color={COLORS[i % COLORS.length]}
              rightLabel={`${fmtB(d.monto_orig_b)} · LTV ${fmtLtv(d.ltv_promedio_pct)} · ${d.pct_monto?.toFixed(1)}%`}
            />
          ))
        }
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════
// SECCIÓN: RIESGO (HHI + Anomalías)
// ══════════════════════════════════════════════════════════════════════
const HHI_DESC_VIV = `El Índice Herfindahl-Hirschman (HHI) mide la concentración institucional dentro de cada segmento hipotecario (Interés Social, Media/Residencial, INFONAVIT, etc.). Saldo mínimo analizado: 500 MDP. Escala 0–10,000: < 1,500 competitivo, 1,500–2,500 moderado, > 2,500 alta concentración.`;

const ANOM_DESC_VIV = `Detección de deterioro acelerado por z-score del delta mensual de IMOR. Por cada par (institución, segmento_vivienda) con saldo ≥ 500 MDP, se calcula la media y desviación estándar histórica del cambio mensual en IMOR. Se reportan casos donde el cambio actual supera 2 desviaciones estándar y el delta absoluto es ≥ 0.3 pp.`;

function SeccionRiesgo({ s }) {
  const conc    = s?.concentracion || {};
  const anom    = s?.anomalias     || {};
  const numAnom = (anom.filas || []).filter(f => f.z_score > 0).length;

  const catColor = cat =>
    cat === "Alta" ? COLOR.red : cat === "Moderada" ? COLOR.amber : COLOR.green;

  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
      {/* ── HHI ── */}
      <div>
        <div style={{ fontSize: 13, color: COLOR.muted, fontFamily: "monospace",
                      textTransform: "uppercase", letterSpacing: ".5px", marginBottom: 8 }}>
          Concentración HHI — por Segmento
        </div>
        <div style={{ fontSize: 13, color: COLOR.dim, fontFamily: "monospace", lineHeight: 1.6,
                      marginBottom: 10, background: COLOR.surface, border: `1px solid ${COLOR.border}`,
                      borderRadius: 4, padding: "6px 10px" }}>
          {HHI_DESC_VIV}
        </div>
        {conc.insight && (
          <div style={{ fontSize: 14, color: COLOR.muted, lineHeight: 1.6, marginBottom: 10 }}>
            {conc.insight}
          </div>
        )}
        {(conc.filas || []).map((f, i) => (
          <div key={i} style={{ display: "flex", justifyContent: "space-between",
                                padding: "5px 0", borderBottom: `1px solid ${COLOR.border}33`,
                                fontSize: 14, fontFamily: "monospace" }}>
            <div style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                          paddingRight: 8 }}>
              <span style={{ color: catColor(f.categoria_hhi), marginRight: 6, fontSize: 12 }}>●</span>
              <span style={{ color: COLOR.text }}>{f.segmento_vivienda}</span>
            </div>
            <div style={{ textAlign: "right", flexShrink: 0 }}>
              <span style={{ color: catColor(f.categoria_hhi), fontWeight: 700 }}>
                {f.hhi_actual?.toFixed(0)}
              </span>
              <span style={{ color: COLOR.dim, marginLeft: 6 }}>
                {f.direccion === "Aumenta" ? "▲" : f.direccion === "Disminuye" ? "▼" : "─"}
              </span>
              <span style={{ color: COLOR.dim, marginLeft: 6, fontSize: 13 }}>
                {f.banco_dominante} ({f.cuota_dominante_pct?.toFixed(1)}%)
              </span>
            </div>
          </div>
        ))}
      </div>

      {/* ── Anomalías ── */}
      <div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
          <div style={{ fontSize: 13, color: COLOR.muted, fontFamily: "monospace",
                        textTransform: "uppercase", letterSpacing: ".5px" }}>
            Anomalías estadísticas IMOR
          </div>
          {numAnom > 0 && (
            <span style={{ background: `${COLOR.red}22`, color: COLOR.red,
                           borderRadius: 10, padding: "1px 8px", fontSize: 13, fontFamily: "monospace" }}>
              {numAnom} deterioro{numAnom > 1 ? "s" : ""}
            </span>
          )}
        </div>
        <div style={{ fontSize: 13, color: COLOR.dim, fontFamily: "monospace", lineHeight: 1.6,
                      marginBottom: 10, background: COLOR.surface, border: `1px solid ${COLOR.border}`,
                      borderRadius: 4, padding: "6px 10px" }}>
          {ANOM_DESC_VIV}
        </div>
        {(anom.filas || []).length === 0
          ? <div style={{ fontSize: 14, color: COLOR.green, fontFamily: "monospace" }}>
              Sin anomalías detectadas en el periodo.
            </div>
          : (anom.filas || []).map((f, i) => (
            <div key={i} style={{ padding: "7px 0", borderBottom: `1px solid ${COLOR.border}33`,
                                  fontSize: 14, fontFamily: "monospace" }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 2 }}>
                <span style={{ color: COLOR.text, fontWeight: 600 }}>{f.nombre_entidad}</span>
                <span style={{ color: f.z_score > 0 ? COLOR.red : COLOR.green, fontWeight: 700 }}>
                  z={f.z_score?.toFixed(2)}
                </span>
              </div>
              <div style={{ color: COLOR.muted, fontSize: 13 }}>
                {f.actividad_economica} · IMOR {fmtImor(f.imor)} · Δ {f.delta_imor_pp > 0 ? "+" : ""}{f.delta_imor_pp?.toFixed(2)} pp
              </div>
              <div style={{ marginTop: 2 }}>
                <span style={{
                  fontSize: 12, fontFamily: "monospace", padding: "1px 6px", borderRadius: 3,
                  background: f.z_score > 0 ? `${COLOR.red}22` : `${COLOR.green}22`,
                  color: f.z_score > 0 ? COLOR.red : COLOR.green,
                  border: `1px solid ${f.z_score > 0 ? COLOR.red : COLOR.green}44`,
                }}>
                  {f.tipo_anomalia}
                </span>
              </div>
            </div>
          ))
        }
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════
// COMPONENTE PRINCIPAL
// ══════════════════════════════════════════════════════════════════════
export default function ReporteVivienda({ user }) {
  const [modo,         setModo]         = useState("sectorial");
  const [institucion,  setInstitucion]  = useState("");
  const [periodo,      setPeriodo]      = useState(PERIODOS[0]);
  const [loading,      setLoading]      = useState(false);
  const [error,        setError]        = useState(null);
  const [reporte,      setReporte]      = useState(null);
  const [seccionActiva,setSeccionActiva]= useState("resumen");
  const [exportando,   setExportando]   = useState(false);

  // ── Descarga HTML ───────────────────────────────────────────────────
  const exportarHTML = () => {
    if (!reporte) return;
    setExportando(true);
    try {
      const html = generarHTMLExport(reporte);
      const blob = new Blob([html], { type: "text/html;charset=utf-8" });
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement("a");
      const per  = String(reporte.periodo);
      a.href     = url;
      a.download = `reporte_vivienda_${per}_${(reporte.nombre_entidad || "sectorial").replace(/\s+/g, "_")}.html`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error("Error al exportar:", e);
    } finally {
      setExportando(false);
    }
  };

  const generarReporte = async () => {
    if (modo === "institucional" && !institucion) {
      setError("Selecciona una institución.");
      return;
    }
    setLoading(true);
    setError(null);
    setReporte(null);
    try {
      const body = {
        modo,
        nombre_entidad: modo === "institucional" ? institucion : undefined,
        periodo: parseInt(periodo),
        usuario: user?.email || "",
      };
      const res = await fetch(`${API_URL}/reporte/vivienda`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `Error ${res.status}`);
      }
      const data = await res.json();
      setReporte(data);
      setSeccionActiva("resumen");
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const s = reporte?.secciones;

  const numAnom = s
    ? ((s.anomalias?.filas || []).filter(f => f.z_score > 0).length)
    : 0;

  return (
    <div style={{ display: "flex", flexDirection: "column", flex: 1, overflow: "hidden",
                  background: COLOR.bg }}>

      {/* TOP BAR */}
      <div style={{
        display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap",
        padding: "10px 20px", background: COLOR.surface,
        borderBottom: `1px solid ${COLOR.border}`, flexShrink: 0,
      }}>
        {["sectorial", "institucional"].map(m => (
          <button key={m} onClick={() => setModo(m)} style={{
            padding: "5px 14px", borderRadius: 4, fontSize: 14,
            fontFamily: "monospace", cursor: "pointer",
            border: `1px solid ${modo === m ? COLOR.gold : COLOR.border}`,
            background: modo === m ? `${COLOR.gold}18` : "none",
            color: modo === m ? COLOR.gold : COLOR.muted,
          }}>
            {m === "sectorial" ? "Sectorial" : "Institucional"}
          </button>
        ))}

        {modo === "institucional" && (
          <select value={institucion} onChange={e => setInstitucion(e.target.value)} style={{
            background: COLOR.card, color: COLOR.text, border: `1px solid ${COLOR.border}`,
            borderRadius: 4, padding: "5px 10px", fontSize: 14, fontFamily: "monospace",
          }}>
            <option value="">— Seleccionar —</option>
            {INSTITUCIONES_VIV.map(i => <option key={i} value={i}>{i}</option>)}
          </select>
        )}

        <select value={periodo} onChange={e => setPeriodo(e.target.value)} style={{
          background: COLOR.card, color: COLOR.text, border: `1px solid ${COLOR.border}`,
          borderRadius: 4, padding: "5px 10px", fontSize: 14, fontFamily: "monospace",
        }}>
          {PERIODOS.map(p => (
            <option key={p} value={p}>{p.slice(0,4)}-{p.slice(4)}</option>
          ))}
        </select>

        <button onClick={generarReporte} disabled={loading} style={{
          padding: "5px 18px", borderRadius: 4, fontSize: 14, fontFamily: "monospace",
          cursor: loading ? "wait" : "pointer",
          border: `1px solid ${COLOR.gold}`,
          background: loading ? COLOR.surface : `${COLOR.gold}22`,
          color: loading ? COLOR.muted : COLOR.gold, fontWeight: 700,
        }}>
          {loading ? "Generando…" : "Generar reporte"}
        </button>

        {reporte && (
          <button onClick={exportarHTML} disabled={exportando} title="Descargar informe como HTML" style={{
            padding: "5px 14px", borderRadius: 4, fontSize: 14, fontFamily: "monospace",
            cursor: "pointer", border: `1px solid ${COLOR.border}`,
            background: "none", color: COLOR.muted,
          }}>
            {exportando ? "…" : "↓ HTML"}
          </button>
        )}

        {reporte && (
          <span style={{ fontSize: 13, color: COLOR.dim, fontFamily: "monospace", marginLeft: "auto" }}>
            🏠 {reporte.nombre_entidad} · {String(reporte.periodo).slice(0,4)}-{String(reporte.periodo).slice(4)} · {reporte.tiempo_s}s
          </span>
        )}
      </div>

      {/* ERROR */}
      {error && (
        <div style={{ padding: "10px 20px", background: `${COLOR.red}22`,
                      color: COLOR.red, fontSize: 15, fontFamily: "monospace", flexShrink: 0 }}>
          ⚠ {error}
        </div>
      )}

      {/* LOADING */}
      {loading && (
        <div style={{ padding: "40px 20px", textAlign: "center", flex: 1 }}>
          <div style={{ fontSize: 15, color: COLOR.muted, fontFamily: "monospace" }}>
            Consultando BigQuery y generando narrativas…
          </div>
          <div style={{ marginTop: 8, fontSize: 13, color: COLOR.dim, fontFamily: "monospace" }}>
            Cartera de Vivienda · {periodo.slice(0,4)}-{periodo.slice(4)}
          </div>
        </div>
      )}

      {/* CONTENIDO */}
      {reporte && !loading && (
        <div style={{ display: "flex", flex: 1, overflow: "hidden" }}>

          {/* SIDEBAR */}
          <div style={{
            width: 160, flexShrink: 0, background: COLOR.surface,
            borderRight: `1px solid ${COLOR.border}`,
            overflowY: "auto", padding: "12px 0",
          }}>
            {SECCIONES.map(sec => (
              <button key={sec.id} onClick={() => setSeccionActiva(sec.id)} style={{
                width: "100%", textAlign: "left",
                padding: "8px 16px", fontSize: 14, fontFamily: "monospace",
                cursor: "pointer", border: "none",
                background: seccionActiva === sec.id ? `${COLOR.gold}18` : "none",
                color: seccionActiva === sec.id ? COLOR.gold : COLOR.muted,
                borderLeft: `2px solid ${seccionActiva === sec.id ? COLOR.gold : "transparent"}`,
                display: "flex", alignItems: "center", gap: 6,
              }}>
                <span>{sec.icon}</span>
                <span>{sec.label}</span>
                {sec.id === "riesgo" && numAnom > 0 && (
                  <span style={{ marginLeft: "auto", background: `${COLOR.red}22`, color: COLOR.red,
                                 borderRadius: 8, padding: "0 5px", fontSize: 12, fontFamily: "monospace" }}>
                    {numAnom}
                  </span>
                )}
              </button>
            ))}
          </div>

          {/* MAIN PANEL */}
          <div style={{ flex: 1, overflowY: "auto", padding: "20px 24px" }}>
            {seccionActiva === "resumen" && (
              <>
                <SectionHeader icon="📋" title="Resumen Ejecutivo — Cartera de Vivienda" />
                <SeccionResumen s={s} />
              </>
            )}
            {seccionActiva === "calidad" && (
              <>
                <SectionHeader icon="◉" title="Calidad de Cartera — IFRS 9" />
                <SeccionCalidad s={s} />
              </>
            )}
            {seccionActiva === "rollrates" && (
              <>
                <SectionHeader icon="⟳" title="Migración de Saldos (Roll Rates)" />
                <SeccionRollRates s={s} />
              </>
            )}
            {seccionActiva === "destino" && (
              <>
                <SectionHeader icon="◆" title="Por Destino de Crédito" />
                <SeccionDestino s={s} />
              </>
            )}
            {seccionActiva === "territorio" && (
              <>
                <SectionHeader icon="🗺️" title="Distribución Territorial" />
                <SeccionTerritorio s={s} />
              </>
            )}
            {seccionActiva === "originacion" && (
              <>
                <SectionHeader icon="⊕" title="Originación Hipotecaria" />
                <SeccionOriginacion s={s} />
              </>
            )}
            {seccionActiva === "riesgo" && (
              <>
                <SectionHeader icon="🔍" title="Análisis de Riesgo — HHI y Anomalías" />
                <SeccionRiesgo s={s} />
              </>
            )}

            {/* Errores de sección */}
            {reporte.errores && Object.keys(reporte.errores).length > 0 && (
              <div style={{ marginTop: 24, padding: "10px 14px",
                            background: `${COLOR.amber}0d`, border: `1px solid ${COLOR.amber}44`,
                            borderRadius: 6, fontSize: 13, fontFamily: "monospace", color: COLOR.amber }}>
                ⚠ Secciones con error: {Object.keys(reporte.errores).join(", ")}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ESTADO INICIAL */}
      {!reporte && !loading && !error && (
        <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={{ textAlign: "center", color: COLOR.dim, fontFamily: "monospace" }}>
            <div style={{ fontSize: 32, marginBottom: 12 }}>🏠</div>
            <div style={{ fontSize: 16, color: COLOR.muted, marginBottom: 6 }}>
              Reporte de Cartera de Vivienda
            </div>
            <div style={{ fontSize: 14 }}>
              Selecciona modo y periodo, luego presiona <span style={{ color: COLOR.gold }}>Generar reporte</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
